"use client";

import { usePage } from "@inertiajs/react";
import NumberFlow from "@number-flow/react";
import { WandSparkles } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { PlanCatalogTier } from "@/types";

type BillingInterval = "monthly" | "yearly";

interface TierConfig {
  pages: string;
  monthlyPrice: number;
  yearlyPrice: number;
  credits: number;
  support: string;
}

const FALLBACK_TIERS: TierConfig[] = [
  {
    pages: "10k",
    monthlyPrice: 5,
    yearlyPrice: 50,
    credits: 0,
    support: "Email support",
  },
  {
    pages: "100k",
    monthlyPrice: 49,
    yearlyPrice: 99,
    credits: 100,
    support: "Email support",
  },
  {
    pages: "100k+",
    monthlyPrice: 99,
    yearlyPrice: 199,
    credits: 200,
    support: "Priority support",
  },
];

export function PricingTable() {
  const { plan_catalog } = usePage().props;
  const [tierIndex, setTierIndex] = useState<number>(0);
  const [billingInterval, setBillingInterval] = useState<BillingInterval>("yearly");

  const tiers = useMemo<TierConfig[]>(() => {
    if (!plan_catalog?.length) return FALLBACK_TIERS;

    return plan_catalog.map((tier: PlanCatalogTier) => ({
      pages: tier.pages,
      monthlyPrice: tier.monthly_price,
      yearlyPrice: tier.yearly_price,
      credits: tier.ai_credits,
      support: tier.support,
    }));
  }, [plan_catalog]);

  useEffect(() => {
    const maxIndex = Math.max(tiers.length - 1, 0);
    if (tierIndex > maxIndex) {
      setTierIndex(maxIndex);
    }
  }, [tierIndex, tiers.length]);

  const maxTierIndex = Math.max(tiers.length - 1, 0);
  const currentTier = tiers[Math.min(tierIndex, maxTierIndex)] || FALLBACK_TIERS[0];
  const monthlyPrice = currentTier.monthlyPrice;
  const yearlyPrice = currentTier.yearlyPrice;
  const displayPrice = billingInterval === "monthly" ? monthlyPrice : yearlyPrice;
  const priceLabel = billingInterval === "monthly" ? "/month" : "/year";

  const handleSliderChange = (value: number[]) => {
    setTierIndex(value[0]);
  };

  const handleBillingChange = (value: string) => {
    if (value === "monthly" || value === "yearly") {
      setBillingInterval(value);
    }
  };

  return (
    <div className="mx-auto w-full max-w-2xl space-y-8 px-4 sm:px-6 lg:max-w-3xl">
      {/* Controls Section */}
      <div className="flex flex-col gap-12">
        {/* Slider Section */}
        <div className="flex-1 space-y-4">
          <div className="space-y-3">
            <div className="relative flex items-center gap-3">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">10k</span>
              <div className="relative flex-1">
                <Slider
                  value={[tierIndex]}
                  onValueChange={handleSliderChange}
                  min={0}
                  max={maxTierIndex}
                  step={1}
                  className="w-full cursor-pointer"
                />
                <div
                  className="absolute -top-12 -translate-x-1/2 transform transition-all duration-300 ease-out"
                  style={{
                    left: `clamp(1%, ${(tierIndex / Math.max(maxTierIndex, 1)) * 100}%, 99%)`,
                  }}
                >
                  <div className="relative rounded-lg bg-gray-900 px-3 py-2 text-xs font-bold whitespace-nowrap text-white shadow-xl ring-1 ring-white/10 dark:bg-gray-800">
                    {currentTier.pages === "10k"
                      ? "Up to 10k pages"
                      : currentTier.pages === "100k"
                        ? "Up to 100k pages"
                        : "Unlimited pages"}
                    {/* Arrow for tooltip */}
                    <div className="absolute -bottom-1 left-1/2 h-2 w-2 -translate-x-1/2 rotate-45 bg-gray-900 dark:bg-gray-800"></div>
                  </div>
                </div>
              </div>
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">100k+</span>
            </div>
          </div>
        </div>

        {/* Billing Toggle Section */}
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <ToggleGroup
              type="single"
              value={billingInterval}
              onValueChange={handleBillingChange}
              className="border border-gray-200 bg-gray-100 p-1 dark:border-gray-700 dark:bg-gray-800"
            >
              <ToggleGroupItem
                value="monthly"
                aria-label="Monthly billing"
                className={`rounded-md px-4 py-2 ${
                  billingInterval === "monthly"
                    ? "!bg-white text-gray-900 shadow-sm dark:!bg-gray-600 dark:text-white"
                    : "bg-gray-100 text-gray-500 hover:text-gray-900 dark:bg-gray-800 dark:text-gray-400 dark:hover:text-white"
                }`}
              >
                Monthly
              </ToggleGroupItem>
              <ToggleGroupItem
                value="yearly"
                aria-label="Yearly billing"
                className={`rounded-md px-8 py-2 ${
                  billingInterval === "yearly"
                    ? "!bg-white text-gray-900 shadow-sm dark:!bg-gray-600 dark:text-white"
                    : "bg-gray-100 text-gray-500 hover:text-gray-900 dark:bg-gray-800 dark:text-gray-400 dark:hover:text-white"
                }`}
              >
                Yearly
                <div className="flex items-center gap-1 rounded-md bg-orange-500/10 p-1 text-xs font-bold text-orange-600 dark:bg-orange-900/40 dark:text-orange-400">
                  <span>🔥</span>
                  <span>Save ${Intl.NumberFormat("en-US").format(monthlyPrice * 12 - yearlyPrice)}</span>
                </div>
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        </div>
      </div>

      {/* Pricing Card */}
      <Card className="relative mx-auto w-full max-w-sm border-gray-200 bg-white/50 shadow-xl backdrop-blur-sm dark:border-gray-800 dark:bg-gray-950/50">
        <CardHeader className="pb-2 text-center">
          <div className="mt-3 flex items-center justify-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
            <WandSparkles className="h-4 w-4" />
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="border-base-content/30 hover:border-primary cursor-help border-b-2 border-dashed duration-150 hover:border-solid dark:border-orange-500">
                  {currentTier.credits} credits
                </span>
              </TooltipTrigger>
              <TooltipContent
                className="max-w-xs dark:bg-gray-900 dark:text-neutral-50"
                arrowClassName="dark:bg-gray-900 dark:fill-gray-900"
              >
                <p>
                  1 credit = 1 image generated with our state-of-the-art image model. <br />
                  <br /> Credits refresh {billingInterval === "monthly" ? "monthly" : "annually"} and are automatically
                  credited to your account. Extra credits cost $9 per 30 credits ($0.30 per credit).
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="mt-4 flex items-baseline justify-center gap-1">
            <span className="text-5xl font-extrabold tracking-tight text-orange-600 dark:text-orange-500">
              <NumberFlow
                value={displayPrice}
                format={{
                  style: "currency",
                  currency: "USD",
                  currencyDisplay: "symbol",
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }}
                transformTiming={{
                  duration: 300,
                  easing: "ease-out",
                }}
              />
            </span>
            <span className="text-lg font-medium text-gray-500 dark:text-gray-400">{priceLabel}</span>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <ul className="space-y-2 text-sm">
            <li className="flex items-center gap-2">
              <span className="text-green-500">✓</span>
              <span className="font-medium">Unlimited domains</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-green-500">✓</span>
              <span className="font-medium">Unlimited image hits</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-green-500">✓</span>
              <span className="font-medium">
                {currentTier.pages === "10k"
                  ? "Up to 10,000 pages"
                  : currentTier.pages === "100k"
                    ? "Up to 100,000 pages"
                    : "Unlimited pages"}
              </span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-green-500">✓</span>
              <span>Global CDN delivery</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-green-500">✓</span>
              <span>Social media analytics</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-green-500">✓</span>
              <span>{currentTier.support}</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
